import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IoRocket, IoClose, IoCheckmarkCircle } from 'react-icons/io5';
import { doc, updateDoc, serverTimestamp, addDoc, collection } from 'firebase/firestore';
import { db } from '../firebase';
import { usePlumes } from './Plumes';
import { useUser } from '../context/UserContext';
import { useNavigate } from 'react-router-dom';
import './BoostBook.css';

const BOOST_OPTIONS = [
  { amount: 100, duration: 1, label: 'Boost Express', sub: '24 heures', emoji: '⚡' },
  { amount: 300, duration: 3, label: 'Boost Pro', sub: '3 jours', emoji: '🚀' },
  { amount: 700, duration: 7, label: 'Boost Premium', sub: '7 jours', emoji: '💎' },
];

export default function BoostBook({ bookId, bookTitle }) {
  const { user } = useUser();
  const { plumes, spendPlumes } = usePlumes();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const canAffordAny = BOOST_OPTIONS.some(opt => plumes >= opt.amount);

  const handleBoost = async (option) => {
    setError('');
    if (plumes < option.amount) {
      setError('not_enough');
      return;
    }

    setLoading(true);
    try {
      const ok = await spendPlumes(option.amount, `Boost "${bookTitle}" — ${option.label}`);
      if (!ok) {
        setError('Erreur lors du paiement.');
        setLoading(false);
        return;
      }

      const boostUntil = new Date();
      boostUntil.setDate(boostUntil.getDate() + option.duration);

      await updateDoc(doc(db, 'books', bookId), {
        boosted: true,
        boostUntil: boostUntil.toISOString(),
        boostLevel: option.duration,
      });

      await addDoc(collection(db, 'boosts'), {
        bookId,
        bookTitle,
        userId: user.id,
        userName: user.name,
        plumesSpent: option.amount,
        duration: option.duration,
        boostUntil: boostUntil.toISOString(),
        createdAt: serverTimestamp(),
      });

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setIsOpen(false);
      }, 2500);
    } catch (e) {
      console.error('Erreur boost:', e);
      setError('Erreur. Réessaie.');
    }
    setLoading(false);
  };

  return (
    <>
      <motion.button
        className="boost-trigger"
        onClick={() => setIsOpen(true)}
        whileTap={{ scale: 0.9 }}
      >
        <IoRocket /> Booster
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              className="boost-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => { setIsOpen(false); setError(''); }}
            />
            <motion.div
              className="boost-panel"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
            >
              {success ? (
                <div className="boost-success">
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring' }} className="boost-success-icon">
                    <IoCheckmarkCircle />
                  </motion.div>
                  <h3>Livre boosté ! 🚀</h3>
                  <p>"{bookTitle}" sera mis en avant auprès de tous les lecteurs.</p>
                </div>
              ) : (
                <>
                  <div className="boost-header">
                    <h3>Booster "{bookTitle}"</h3>
                    <button className="boost-close" onClick={() => { setIsOpen(false); setError(''); }}><IoClose /></button>
                  </div>
                  <p className="boost-subtitle">Le livre sera mis en avant dans Explorer et prioritaire dans le Swipe</p>
                  <p className="boost-balance">Ton solde : <strong>{plumes.toLocaleString('fr-FR')} 🪶</strong></p>

                  {/* Pas assez de plumes — bouton achat */}
                  {(!canAffordAny || error === 'not_enough') && (
                    <div className="boost-buy-section">
                      <p className="boost-buy-text">
                        {plumes === 0 ? 'Tu n\'as pas encore de plumes' : `Pas assez de plumes (${plumes} 🪶)`}
                      </p>
                      <motion.button
                        className="boost-buy-link"
                        onClick={() => { setIsOpen(false); navigate('/profile'); }}
                        whileTap={{ scale: 0.95 }}
                      >
                        🪶 Acheter des plumes
                      </motion.button>
                    </div>
                  )}

                  {error && error !== 'not_enough' && (
                    <p className="boost-error">{error}</p>
                  )}

                  <div className="boost-options">
                    {BOOST_OPTIONS.map(opt => (
                      <motion.button
                        key={opt.amount}
                        className={'boost-option ' + (plumes < opt.amount ? 'boost-option-disabled' : '')}
                        onClick={() => handleBoost(opt)}
                        disabled={loading}
                        whileTap={plumes >= opt.amount ? { scale: 0.95 } : {}}
                        whileHover={plumes >= opt.amount ? { y: -2 } : {}}
                      >
                        <span className="boost-option-emoji">{opt.emoji}</span>
                        <strong className="boost-option-label">{opt.label}</strong>
                        <span className="boost-option-sub">{opt.sub}</span>
                        <span className="boost-option-price">{opt.amount} 🪶</span>
                      </motion.button>
                    ))}
                  </div>
                </>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}